import { Pipe, PipeTransform } from '@angular/core';

/** Separador de miles chileno para las antigüedades largas ("1.234 días"). */
const NUMBER_FORMATTER = new Intl.NumberFormat('es-CL');

/** Lo que el pipe sabe leer: el `Timestamp` del proto o una fecha del navegador. */
export type RelativeDaysInput = Date | { toDate(): Date } | null | undefined;

/**
 * Antigüedad en días, en español neutro: "Hoy", "1 día", "N días" (feature 035).
 *
 * Es el PRIMER formateador de tiempo relativo reutilizable de bp-advisor: el CRM ya arrastra cinco
 * formateadores de fecha absolutos copiados componente por componente, y bp-admin tiene el suyo
 * duplicado como método. Este vive en `shared/pipes` para que el próximo no vuelva a copiarse.
 *
 * La cuenta es por DÍA CALENDARIO LOCAL, no por múltiplos de 24 horas: algo de ayer a las 23:50
 * es "1 día" aunque hayan pasado 20 minutos. Sin valor devuelve cadena vacía — nunca `NaN` ni
 * "Invalid Date" en pantalla.
 */
@Pipe({
  name: 'relativeDays',
  standalone: true,
})
export class RelativeDaysPipe implements PipeTransform {
  transform(value: RelativeDaysInput, now: Date = new Date()): string {
    const date = toDate(value);
    if (!date) {
      return '';
    }

    const days = calendarDaysBetween(date, now);
    if (days <= 0) {
      // Una fecha futura (desfase de reloj entre servidor y navegador) se lee como hoy: mostrar
      // "-1 días" sería peor que redondear al presente.
      return 'Hoy';
    }

    return days === 1 ? '1 día' : `${NUMBER_FORMATTER.format(days)} días`;
  }
}

/** Días calendario transcurridos entre dos instantes, en la zona horaria del navegador. */
export function calendarDaysBetween(from: Date, to: Date): number {
  const fromDay = startOfDay(from).getTime();
  const toDay = startOfDay(to).getTime();
  return Math.round((toDay - fromDay) / 86_400_000);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function toDate(value: RelativeDaysInput): Date | null {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : value.toDate();
  return Number.isNaN(date.getTime()) ? null : date;
}
